// ============================================================================
// CUSTOS — Prediction Store (signalState-based)
// Owns all prediction market state: markets, cards, activities, bet placement.
// ============================================================================

import { Injectable, computed, inject } from '@angular/core';
import { patchState, signalState } from '@ngrx/signals';
import {
  Observable,
  catchError,
  forkJoin,
  from,
  map,
  of,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { parseUnits, WaitForTransactionReceiptTimeoutError, type Address } from 'viem';

import type {
  ActivityEntry,
  BetRequest,
  BetResult,
  HomeActivity,
  MarketCardData,
  MarketFilter,
  PredictionMarket,
} from '../../shared/models';
import {
  apiMarketAndCycleToMarketCard,
  apiMarketAndCycleToPredictionMarket,
  apiOrderToActivityEntry,
  apiOrderToHomeActivity,
  apiLockerOrderToHomeActivity,
  computeWinMultiplier,
  parseMarketId,
  safeNum,
  deriveLockerAmounts,
  truncateWeiToCents,
} from '../../core/data/data-transform';
import { API_SERVICE, type CustosApi } from '../../core/api/api.provider';
import type { ApiCycle, ApiMarket } from '../../core/api/api.types';
import { MarketCacheService } from '../../core/services/market-cache.service';
import { WalletService } from '../../core/services/wallet';
import { CUSTOS_ENVIRONMENT, type CustosEnvironment } from '../../core/models/environment';
import { PREDICT_MARKET_ABI } from '../../core/contracts/abis/predict-market.abi';
import { ERC20_ABI } from '../../core/contracts/abis/erc20.abi';
import { TOKEN_LOCKER_ABI } from '../../core/contracts/abis/token-locker.abi';

// ============================================================================
// TYPES
// ============================================================================

interface BetParams {
  cycleId: bigint;
  betAmount: bigint;
  direction: number;
  share: bigint;
  nonce: bigint;
  deadline: bigint;
  betSignature: `0x${string}`;
}

// ============================================================================
// STATE
// ============================================================================

interface UnsettledInfo {
  orderIds: number[];
  settleable: boolean;
  pendingCycleIds: number[];
  checkedAt: number;
}

interface PredictionStoreState {
  markets: PredictionMarket[];
  filter: MarketFilter;
  marketCards: MarketCardData[];
  homeActivities: HomeActivity[];
  loadingCount: number;
  error: string | null;
  marketsFetchedAt: number | null;
  marketCardsFetchedAt: number | null;
  homeActivitiesFetchedAt: number | null;

  // Per-market cycles (keyed by marketId)
  cyclesPerMarket: Record<string, ApiCycle[]>;
  cyclesFetchedAt: Record<string, number>;

  // Per-market unsettled orders check
  unsettledPerMarket: Record<string, UnsettledInfo>;

  // Per-market locker balance — display number (truncated to 2 decimals)
  // and exact wei stored side-by-side so the bet flow can submit the exact
  // on-chain amount without a Number → parseUnits round-trip.
  //
  // We store the RAW values (wei + endTime + decimals) and derive the
  // spendable amount at selector-read time via deriveLockerAmounts. This
  // way a dialog opened just before endTime returns 0 once the user hits
  // submit past endTime, without needing a refetch (Codex 2026-05-25
  // observed the stale-after-expiry edge case).
  marketLockerBalances: Record<string, number>;
  marketLockerBalancesWei: Record<string, bigint>;
  marketLockerEndTimes: Record<string, number>;
  marketLockerDecimals: Record<string, number>;

  // Per-market "is a Locker wired on the PredictMarket contract?" flag.
  // Undefined = unknown (not probed yet); true = PredictMarket.locker() returned
  // a non-zero address; false = zero address (e.g. BTC/USDT market with no
  // Rollocker). Bet dialog disables the Rollocker tab when false.
  marketHasLockerFlags: Record<string, boolean>;

  // Per-market "allLocks read failed" flag. hasLocker is true but the balance
  // read itself blew up (RPC flake, ABI mismatch). UI renders "unavailable"
  // instead of a misleading "0 available" with the tab enabled.
  marketLockerBalanceErrors: Record<string, boolean>;
}

const INITIAL_STATE: PredictionStoreState = {
  markets: [],
  filter: 'all',
  marketCards: [],
  homeActivities: [],
  loadingCount: 0,
  error: null,
  marketsFetchedAt: null,
  marketCardsFetchedAt: null,
  homeActivitiesFetchedAt: null,
  cyclesPerMarket: {},
  cyclesFetchedAt: {},
  unsettledPerMarket: {},
  marketLockerBalances: {},
  marketLockerBalancesWei: {},
  marketLockerEndTimes: {},
  marketLockerDecimals: {},
  marketHasLockerFlags: {},
  marketLockerBalanceErrors: {},
};

// Cache duration: 2 minutes
const CACHE_DURATION_MS = 2 * 60 * 1000;

/** Upper bound on how long we wait for the env public client to see an
 *  approve receipt before proceeding to the bet write. The env RPC can
 *  lag the wallet's RPC by several seconds on BSC; waiting forever
 *  strands the user (Codex 2026-05-21 round-3 of P0b reviews). 30s gives
 *  most healthy RPCs time to converge while bounding the worst case. */
const APPROVE_RECEIPT_TIMEOUT_MS = 30_000;

/** Per-contract cache of base token info (immutable on-chain, so cache indefinitely). */
interface TokenInfo { address: Address; symbol: string; decimals: number; }


// ============================================================================
// STORE
// ============================================================================

@Injectable({ providedIn: 'root' })
export class PredictionStore {
  private readonly api = inject<CustosApi>(API_SERVICE);
  private readonly marketCache = inject(MarketCacheService);
  private readonly wallet = inject(WalletService);
  private readonly env = inject<CustosEnvironment>(CUSTOS_ENVIRONMENT);
  private readonly state = signalState<PredictionStoreState>(INITIAL_STATE);
  private readonly baseTokenCache = new Map<Address, TokenInfo>();

  // =========================================================================
  // Selectors
  // =========================================================================

  readonly isLoading = computed(() => this.state().loadingCount > 0);
  readonly error = computed(() => this.state().error);

  /** All markets, filtered by the active token filter. */
  readonly markets = computed(() => {
    const filter = this.state().filter;
    const all = this.state().markets;
    if (filter === 'all') return all;
    return all.filter((m) => m.tokenSymbol === filter);
  });

  /** Market cards for prediction card components. */
  readonly marketCards = computed(() => this.state().marketCards);

  /** Home activity feed entries. */
  readonly homeActivities = computed(() => this.state().homeActivities);

  /** Whether any markets have been loaded. */
  readonly hasMarkets = computed(() => this.state().markets.length > 0);

  /** Current active filter value. */
  readonly filter = computed(() => this.state().filter);

  // =========================================================================
  // Private helpers
  // =========================================================================

  private get chainId(): number {
    return this.env.chainId;
  }

  private get walletAddress(): Address | null {
    return this.wallet.address();
  }

  private get predictionAddress(): Address {
    return this.env.contracts.prediction as Address;
  }

  /** Read a contract function via the public client. */
  private readContract<T>(args: {
    address: Address;
    abi: readonly unknown[];
    functionName: string;
    args?: readonly unknown[];
  }): Observable<T> {
    const client = this.wallet.getPublicClient();
    return from(
      client.readContract(
        args as Parameters<typeof client.readContract>[0],
      ) as Promise<T>,
    );
  }

  /** Write a contract function via the wallet client. Returns tx hash. */
  private writeContract(args: {
    address: Address;
    abi: readonly unknown[];
    functionName: string;
    args?: readonly unknown[];
    account: Address;
  }): Observable<`0x${string}`> {
    const wc = this.wallet.getWalletClient();
    return from(
      wc.writeContract(args as Parameters<typeof wc.writeContract>[0]),
    );
  }

  /** writeContract with a publicClient-side preflight: simulate first
   *  (throws revert reasons cleanly so we never ship a will-revert tx to
   *  the wallet, which is the inferred root of Wesley's 2026-05-21
   *  "gas abnormally high" report — the wallet was estimating against a
   *  call that would have reverted and surfaced the block gas limit as
   *  the estimate). Then estimate gas via publicClient and pass it
   *  through as an explicit `gas` field so wallet-side estimation can no
   *  longer inflate the displayed fee.
   *
   *  Estimate failure fails CLOSED (Codex 2026-05-21 round-3 of P0b
   *  reviews): if simulate passed but estimate fails, that's RPC
   *  instability on the SAME RPC that just succeeded — falling back to
   *  wallet estimation here would silently re-introduce the exact
   *  failure surface this method exists to remove.
   *
   *  IMPORTANT: only use when there is NO env-RPC vs wallet-RPC state
   *  skew window immediately preceding the write (Codex 2026-05-21
   *  round-2). For paths where we just mined an on-chain state change
   *  (e.g. ERC20 approve), use writeContractWithGasOnly instead — a
   *  load-balanced RPC cluster can return caught-up state on one read
   *  and stale state on the next, so post-approve simulate cannot be
   *  trusted (Codex 2026-05-21 round-4). */
  private writeContractWithPreflight(args: {
    address: Address;
    abi: readonly unknown[];
    functionName: string;
    args?: readonly unknown[];
    account: Address;
  }): Observable<`0x${string}`> {
    const pc = this.wallet.getPublicClient();
    return from(
      pc.simulateContract(args as Parameters<typeof pc.simulateContract>[0]),
    ).pipe(
      switchMap(() =>
        from(
          pc.estimateContractGas(
            args as Parameters<typeof pc.estimateContractGas>[0],
          ),
        ),
      ),
      switchMap((gas) => {
        const wc = this.wallet.getWalletClient();
        return from(
          wc.writeContract({ ...args, gas } as Parameters<typeof wc.writeContract>[0]),
        );
      }),
    );
  }



  /**
   * Read `baseToken()`, then its ERC-20 `symbol()` and `decimals()`.
   * Cached per prediction contract address — all values are immutable once set.
   */
  getBaseTokenInfo(contractAddress: Address): Observable<TokenInfo> {
    const cached = this.baseTokenCache.get(contractAddress);
    if (cached) return of(cached);

    return this.readContract<Address>({
      address: contractAddress,
      abi: PREDICT_MARKET_ABI,
      functionName: 'baseToken',
    }).pipe(
      switchMap((tokenAddress) =>
        forkJoin({
          symbol: this.readContract<string>({
            address: tokenAddress,
            abi: ERC20_ABI,
            functionName: 'symbol',
          }),
          decimals: this.readContract<number>({
            address: tokenAddress,
            abi: ERC20_ABI,
            functionName: 'decimals',
          }),
        }).pipe(
          map((erc20) => {
            const info: TokenInfo = {
              address: tokenAddress,
              symbol: erc20.symbol,
              decimals: erc20.decimals,
            };
            this.baseTokenCache.set(contractAddress, info);
            return info;
          }),
        ),
      ),
    );
  }

  private isCacheValid(fetchedAt: number | null): boolean {
    return fetchedAt !== null && Date.now() - fetchedAt < CACHE_DURATION_MS;
  }

  private startLoading(): void {
    patchState(this.state, { loadingCount: this.state().loadingCount + 1 });
  }

  private stopLoading(): void {
    patchState(this.state, { loadingCount: Math.max(0, this.state().loadingCount - 1) });
  }

  clearError(): void {
    patchState(this.state, { error: null });
  }

  /** Fetch all markets and their latest cycles, return paired data. */
  private fetchMarketsWithCycles(): Observable<
    { market: ApiMarket; cycle: ApiCycle }[]
  > {
    return this.marketCache.getMarkets().pipe(
      // Wesley: hide markets Guy flagged inactive + explicit env blacklist.
      // Detail deep-links and Portfolio still resolve the raw getMarket(...)
      // path so historic positions don't vanish; only listings filter.
      // NOTE: market.token_symbol is the *predicted* asset (ETH for CTS-ETH);
      // the base/staking token comes from parseMarketId(market_id).
      map((markets) => {
        const hidden = new Set((this.env.hiddenLockerIds ?? []).map((s) => s.toUpperCase()));
        return markets.filter((m) => {
          if (m.status === 'inactive') return false;
          const { base } = parseMarketId(m.market_id);
          return !hidden.has(base.toUpperCase());
        });
      }),
      switchMap((markets) => {
        if (markets.length === 0) return of([]);
        const cycleRequests = markets.map((m) =>
          this.api
            .getCycles({
              chain_id: m.chain_id,
              market_id: m.market_id,
              // Backend pre-schedules many future cycles (all SCHEDULED, no
              // cycle_id). The single BETTING cycle sits BELOW that batch in
              // id-DESC order, so a small limit can miss it entirely. Use 50
              // to cover ~20 scheduled + live + recently resolved.
              limit: 500,
            })
            .pipe(
              map((cycles) => {
                // Pick the best cycle: prefer ACTIVE/BETTING, then most recent with a cycle_id
                const bettable = cycles.find((c) => c.state === 'ACTIVE' || c.state === 'BETTING');
                const valid = cycles.find((c) => c.cycle_id != null);
                return { market: m, cycle: bettable ?? valid ?? cycles[0] };
              }),
              catchError(() =>
                of({
                  market: m,
                  cycle: undefined as unknown as ApiCycle,
                }),
              ),
            ),
        );
        return forkJoin(cycleRequests);
      }),
      map((pairs) => {
        // Filter invalid + dedup by market_id
        const seen = new Set<string>();
        return pairs.filter((p) => {
          if (!p.cycle) return false;
          if (seen.has(p.market.market_id)) return false;
          seen.add(p.market.market_id);
          return true;
        });
      }),
      // Pre-warm baseToken cache so it's ready before user reaches bet dialog
      tap((pairs) => {
        for (const { market } of pairs) {
          const addr = market.prediction_contract as Address;
          if (addr && !this.baseTokenCache.has(addr)) {
            try {
              this.getBaseTokenInfo(addr).pipe(take(1), catchError(() => of(null))).subscribe();
            } catch {
              // Swallow errors — pre-warming is best-effort
            }
          }
        }
      }),
    );
  }

  // =========================================================================
  // Actions
  // =========================================================================

  /** Set the active market filter. */
  setFilter(filter: MarketFilter): void {
    patchState(this.state, { filter });
  }

  /**
   * Load prediction markets from API.
   * Uses cache to avoid redundant calls.
   */
  loadMarkets(forceRefresh = false): void {
    if (!forceRefresh && this.isCacheValid(this.state().marketsFetchedAt)) return;

    this.startLoading();

    this.fetchMarketsWithCycles()
      .pipe(
        take(1),
        tap((pairs) => {
          const markets = pairs.map((p) =>
            apiMarketAndCycleToPredictionMarket(p.market, p.cycle),
          );
          this.stopLoading();
          patchState(this.state, {
            markets,
            marketsFetchedAt: Date.now(),
          });
        }),
        catchError((error: Error) => {
          console.error('[PredictionStore] Failed to load markets:', error);
          this.stopLoading();
          patchState(this.state, {
            error: error.message || 'Failed to load markets',
          });
          return of(null);
        }),
      )
      .subscribe();
  }

  /**
   * Load market cards (for home page + prediction card components).
   * Uses cache to avoid redundant calls.
   */
  loadMarketCards(forceRefresh = false): void {
    if (
      !forceRefresh &&
      this.state().marketCards.length > 0 &&
      this.isCacheValid(this.state().marketCardsFetchedAt)
    ) {
      return;
    }

    this.startLoading();

    // Fetch markets + lockers to determine which markets have rollockers
    this.fetchMarketsWithCycles().pipe(
        take(1),
        switchMap((pairs) =>
          this.api.getLockers({ chain_id: this.env.chainId }).pipe(
            catchError(() => of([])),
            take(1),
            map((lockers) => ({ pairs, lockers })),
          ),
        ),
        tap(({ pairs, lockers }) => {
          const lockerIds = new Set(lockers.map((l: { locker_id: string }) => l.locker_id.toUpperCase()));
          const cards = pairs.map((p) => {
            const card = apiMarketAndCycleToMarketCard(p.market, p.cycle);
            card.hasRollocker = lockerIds.has(card.baseTokenSymbol.toUpperCase());
            return card;
          });
          const markets = pairs.map((p) =>
            apiMarketAndCycleToPredictionMarket(p.market, p.cycle),
          );
          this.stopLoading();
          patchState(this.state, {
            marketCards: cards,
            markets,
            marketCardsFetchedAt: Date.now(),
          });
        }),
        catchError((error: Error) => {
          console.error(
            '[PredictionStore] Failed to load market cards:',
            error,
          );
          this.stopLoading();
          patchState(this.state, {
            error: error.message || 'Failed to load market cards',
          });
          return of(null);
        }),
      )
      .subscribe();
  }

  /**
   * Load home activity feed entries.
   */
  loadHomeActivities(forceRefresh = false): void {
    if (
      !forceRefresh &&
      this.state().homeActivities.length > 0 &&
      this.isCacheValid(this.state().homeActivitiesFetchedAt)
    ) {
      return;
    }

    this.startLoading();

    forkJoin({
      markets: this.marketCache.getMarkets().pipe(catchError(() => of([]))),
      lockerOrders: this.api.getLockerOrders({
        chain_id: this.chainId,
        limit: 10,
      }).pipe(catchError(() => of([]))),
    }).pipe(
        take(1),
        switchMap(({ markets, lockerOrders }) => {
          // Rollock activities from locker orders
          const rollockActivities: HomeActivity[] = lockerOrders.map(apiLockerOrderToHomeActivity);

          if (markets.length === 0) return of(rollockActivities);

          // Predict activities from orders
          const orderRequests = markets.slice(0, 5).map((m) =>
            this.api
              .getOrders({
                chain_id: m.chain_id,
                market_id: m.market_id,
                limit: 5,
              })
              .pipe(
                map((orders) => orders.map((o) => apiOrderToHomeActivity(o))),
                catchError(() => of([] as HomeActivity[])),
              ),
          );
          return forkJoin(orderRequests).pipe(
            map((arrays) => [...rollockActivities, ...arrays.flat()]),
          );
        }),
        map((activities) =>
          activities
            .sort((a, b) => b.sortTime.localeCompare(a.sortTime))
            .slice(0, 10),
        ),
        tap((activities) => {
          this.stopLoading();
          patchState(this.state, {
            homeActivities: activities,
            homeActivitiesFetchedAt: Date.now(),
          });
        }),
        catchError((error: Error) => {
          console.error(
            '[PredictionStore] Failed to load home activities:',
            error,
          );
          this.stopLoading();
          patchState(this.state, {
            error: error.message || 'Failed to load home activities',
          });
          return of(null);
        }),
      )
      .subscribe();
  }

  // =========================================================================
  // Sync selectors
  // =========================================================================

  /** Find a market by ID from the current state. */
  getMarketById(id: string): PredictionMarket | undefined {
    return this.state().markets.find((m) => m.id === id);
  }

  // =========================================================================
  // Observable methods (per-market activities, bet placement)
  // =========================================================================

  /**
   * Load user's own orders for a specific market.
   * Returns Observable — not cached in store state (per-market data).
   */
  loadMarketActivities(marketId: string): Observable<ActivityEntry[]> {
    const addr = this.walletAddress;
    if (!addr) return of([]);

    return this.api
      .getOrders({
        chain_id: this.chainId,
        market_id: marketId,
        user_address: addr,
        limit: 50,
      })
      .pipe(
        take(1),
        map((orders) =>
          orders.map((o) => ({
              orderTime: new Date(o.created_at).toLocaleString('sv-SE', {
                dateStyle: 'short',
                timeStyle: 'short',
              }),
              direction: (o.side === 'UP' ? 'Yes' : 'No') as 'Yes' | 'No',
              amount: Number(o.amount),
              betFrom: o.from_locker ? 'Rollock' : 'Wallet',
              winMultiplier: 1.0,
              payout: '---',
            })),
        ),
        catchError(() => of([])),
      );
  }

  /**
   * Load all orders for the latest cycle of a market.
   * Returns Observable — not cached in store state (per-market data).
   */
  loadOverallActivities(marketId: string): Observable<ActivityEntry[]> {
    return this.api
      .getCycles({
        chain_id: this.chainId,
        market_id: marketId,
        limit: 1,
      })
      .pipe(
        take(1),
        switchMap((cycles) => {
          if (cycles.length === 0) return of([]);
          const cycle = cycles[0];
          if (cycle.cycle_id == null) return of([]);
          return this.api.getOrdersByCycle({
            chain_id: this.chainId,
            market_id: marketId,
            cycle_id: cycle.cycle_id,
            limit: 50,
          });
        }),
        map((orders) => orders.map((o) => apiOrderToActivityEntry(o))),
        catchError(() => of([])),
      );
  }

  /**
   * Place a bet on a prediction market.
   * Handles both wallet (EOA) and rollocker fund sources.
   */
  placeBet(request: BetRequest): Observable<BetResult> {
    const addr = this.walletAddress;
    if (!addr) return throwError(() => new Error('Wallet not connected'));

    // Resolve the prediction contract for this market
    const market = this.getMarketById(request.marketId);
    const marketContract = (market?.predictionContract ?? this.predictionAddress) as Address;

    const direction = request.side === 'yes' ? 0 : 1;

    // Read base token info (address + decimals) from cache or chain
    return this.getBaseTokenInfo(marketContract).pipe(
      switchMap((tokenInfo) => {
        // Prefer the wei value the amount-input component computed from
        // either parseUnits(rawString) or BigInt-scaled balance math —
        // bypasses the Number → parseUnits round-trip that drops trailing
        // wei and makes MAX exceed balance. Fall back only when callers
        // haven't adopted the BigInt output yet.
        const amountWei = request.amountWei
          ?? parseUnits(String(request.amount), tokenInfo.decimals);
        // Defensive guard: display and wei are supposed to move in
        // lockstep. A caller that hands us `amount > 0` but `amountWei=0n`
        // would silently submit an empty bet — happens in practice when
        // amount-input's parseUnits rejects input mid-type. Refuse loudly.
        if (amountWei === 0n && request.amount > 0) {
          return throwError(() => new Error(
            `placeBet: amountWei=0 but amount=${request.amount} — ` +
            'likely a trailing-dot / scientific-notation input the UI ' +
            'emitted without sanitising; reject rather than submit 0.',
          ));
        }
        return this.readContract<bigint>({
          address: marketContract,
          abi: PREDICT_MARKET_ABI,
          functionName: 'userNonces',
          args: [addr],
        }).pipe(map((nonce) => ({ nonce, tokenInfo, amountWei })));
      }),
      switchMap(({ nonce, tokenInfo, amountWei }) =>
        this.api
          .getCycles({
            chain_id: this.chainId,
            market_id: request.marketId,
            // See fetchMarketsWithCycles: BETTING sits below the SCHEDULED
            // batch in id-DESC order, so limit must span it. Backend can
            // schedule 50+ future cycles at once, so use the max.
            limit: 500,
          })
          .pipe(
            map((cycles) => {
              const bettable = cycles.find(
                (c) => c.cycle_id != null && (c.state === 'BETTING' || c.state === 'ACTIVE'),
              );
              const valid = cycles.find((c) => c.cycle_id != null);
              return { nonce, tokenInfo, amountWei, cycle: bettable ?? valid };
            }),
          ),
      ),
      switchMap(({ nonce, tokenInfo, amountWei, cycle }) => {
        if (!cycle || cycle.cycle_id == null) {
          throw new Error('No active cycle found');
        }

        const deadline = BigInt(Math.floor(Date.now() / 1000) + 600);

        // share = amount * winMultiplier — totals are pre-normalized at the
        // api.service boundary (see normalizeCycle for the 18 - decimals scale).
        const upNum = safeNum(cycle.side_up_total);
        const downNum = safeNum(cycle.side_down_total);
        const bettingEnd = new Date(cycle.betting_end_time);
        const startTime = new Date(cycle.start_time);
        const now = new Date();
        const totalMs = bettingEnd.getTime() - startTime.getTime();
        const remainMs = Math.max(0, bettingEnd.getTime() - now.getTime());
        const multiplier = computeWinMultiplier({
          side: request.side === 'yes' ? 'YES' : 'NO',
          yesPool: upNum,
          noPool: downNum,
          hoursRemaining: remainMs / 3_600_000,
          totalBettingHours: totalMs / 3_600_000,
        });
        // share in wei = amount in wei * multiplier (use BigInt math to avoid precision loss)
        const PRECISION = 1_000_000n;
        const multiplierScaled = BigInt(Math.round(multiplier * 1_000_000));
        const share = (amountWei * multiplierScaled) / PRECISION;

        return this.api
          .createBetSignature({
            cycle_id: cycle.cycle_id,
            amount: amountWei.toString(),
            direction,
            share: share.toString(),
            nonce: nonce.toString(),
            deadline: deadline.toString(),
            messagesender_address: addr,
            chain_id: this.chainId,
            market_address: marketContract,
          })
          .pipe(
            switchMap((sigResponse) => {
              const betParams = {
                cycleId: BigInt(cycle.cycle_id!),
                betAmount: amountWei,
                direction,
                share,
                nonce,
                deadline,
                betSignature: sigResponse.signature as `0x${string}`,
              };

              if (request.fundSource === 'rollocker') {
                // Read the locker address from the market's prediction contract,
                // then check isPredictOpen on that locker (not the env default).
                return this.readContract<Address>({
                  address: marketContract,
                  abi: PREDICT_MARKET_ABI,
                  functionName: 'locker',
                }).pipe(
                  switchMap((lockerAddr) =>
                    this.readContract<boolean>({
                      address: lockerAddr,
                      abi: TOKEN_LOCKER_ABI,
                      functionName: 'isPredictOpen',
                    }).pipe(
                      switchMap((isOpen) => {
                        if (!isOpen) {
                          throw new Error('Prediction market is not open for rollocker bets');
                        }
                        return this.placeBetFromLocker(addr, amountWei, betParams, marketContract, lockerAddr);
                      }),
                    ),
                  ),
                );
              }

              // betFromEOA: check allowance, approve if needed, then call contract.
              // We track whether an approve fired in *this* flow so the
              // downstream write can pick the right preflight strategy:
              // - allowance already sufficient → safe to simulate
              // - approve just mined → skip simulate (env RPC lags wallet)
              return this.readContract<bigint>({
                address: tokenInfo.address,
                abi: ERC20_ABI,
                functionName: 'allowance',
                args: [addr, marketContract],
              }).pipe(
                switchMap((currentAllowance) => {
                  if (currentAllowance >= amountWei) {
                    return of({ freshApprove: false });
                  }
                  return this.writeContract({
                    address: tokenInfo.address,
                    abi: ERC20_ABI,
                    functionName: 'approve',
                    args: [marketContract, amountWei],
                    account: addr,
                  }).pipe(
                    switchMap((approveTxHash) =>
                      from(
                        this.wallet
                          .getPublicClient()
                          .waitForTransactionReceipt({
                            hash: approveTxHash,
                            timeout: APPROVE_RECEIPT_TIMEOUT_MS,
                          }),
                      ).pipe(
                        map((receipt) => {
                          // Reverted approve = hard failure, NEVER advance
                          // to the bet write (Codex 2026-05-21). The
                          // previous catch-all swallowed reverts and
                          // pushed users into a guaranteed-bad second tx.
                          if (
                            (receipt as { status?: string })?.status === 'reverted'
                          ) {
                            throw new Error('Approve transaction reverted');
                          }
                          return { freshApprove: true };
                        }),
                        catchError((err: Error) => {
                          // Tolerate only the specific timeout class, and
                          // only after re-reading allowance to confirm
                          // the approve actually landed on chain (Codex
                          // 2026-05-21 round-3 of P0b: timeout alone
                          // could mean pending / dropped / replaced
                          // approve, not just RPC lag).
                          if (err instanceof WaitForTransactionReceiptTimeoutError) {
                            return this.readContract<bigint>({
                              address: tokenInfo.address,
                              abi: ERC20_ABI,
                              functionName: 'allowance',
                              args: [addr, marketContract],
                            }).pipe(
                              switchMap((allowanceNow) => {
                                if (allowanceNow >= amountWei) {
                                  return of({ freshApprove: true });
                                }
                                return throwError(
                                  () =>
                                    new Error(
                                      'Approval not yet confirmed on-chain; please retry shortly',
                                    ),
                                );
                              }),
                            );
                          }
                          return throwError(() => err);
                        }),
                      ),
                    ),
                  );
                }),
                switchMap(({ freshApprove }) => {
                  const betArgs = {
                    address: marketContract,
                    abi: PREDICT_MARKET_ABI,
                    functionName: 'betFromEOA',
                    args: [betParams],
                    account: addr,
                  };
                  // Pre-existing allowance: full preflight (no skew
                  // window). Post-approve: plain wallet write — we
                  // skip both simulate AND env-RPC gas estimation
                  // because a load-balanced cluster can return either
                  // a false revert OR an inflated estimate from a
                  // stale backend (Codex 2026-05-21 rounds 4-5 of
                  // P0b). The wallet's own estimator sees its own
                  // post-approve state and is the safer source here.
                  //
                  // Known limitation (Codex round-6 of P0b): the
                  // wallet's own RPC may also be mid-convergence,
                  // so a first-interaction EOA bet (approve+bet in
                  // one flow) can still surface the wallet-side
                  // estimation issue Wesley reported. The proper
                  // structural fix (split into two user gestures, or
                  // EIP-2612 permit) is deferred to a follow-up
                  // slice — this slice closes the higher-frequency
                  // repeat-user (pre-existing allowance) and the
                  // entire rollocker path.
                  return freshApprove
                    ? this.writeContract(betArgs)
                    : this.writeContractWithPreflight(betArgs);
                }),
              );
            }),
          );
      }),
      map((txHash) => ({
        positionId: txHash,
        marketId: request.marketId,
        userPrediction: request.side,
        settledPrice: 0,
        targetPrice: 0,
        outcome: 'won' as const,
        settledAt: new Date(),
      })),
      tap(() => {
        this.wallet.bumpBalanceVersion();
      }),
    );
  }

  /** Handle betFromLocker flow: get transfer hash, user signs, call contract. */
  private placeBetFromLocker(
    addr: Address,
    amountWei: bigint,
    betParams: BetParams,
    marketContract: Address,
    lockerAddress: Address,
  ): Observable<`0x${string}`> {
    return from(
      this.wallet.getPublicClient().readContract({
        address: lockerAddress,
        abi: TOKEN_LOCKER_ABI,
        functionName: 'nonces',
        args: [addr],
      }) as Promise<bigint>,
    ).pipe(
      switchMap((transferNonce) => {
        const transferDeadline = BigInt(Math.floor(Date.now() / 1000) + 600);
        return this.api
          .createTransferMessageHash({
            user_address: addr,
            predict_market_address: marketContract,
            amount: amountWei.toString(),
            nonce: transferNonce.toString(),
            deadline: transferDeadline.toString(),
            locker_address: lockerAddress,
            chain_id: this.chainId,
          })
          .pipe(
            switchMap((transferResponse) => {
              const wc = this.wallet.getWalletClient();
              return from(
                wc.signTypedData({
                  account: addr,
                  ...(transferResponse.typed_data as {
                    domain: Record<string, unknown>;
                    types: Record<
                      string,
                      { name: string; type: string }[]
                    >;
                    primaryType: string;
                    message: Record<string, unknown>;
                  }),
                }),
              ).pipe(
                switchMap((transferSig) =>
                  // Locker path always preflights: no fresh on-chain
                  // state transition immediately preceding, so the
                  // env RPC and the wallet's RPC will agree.
                  this.writeContractWithPreflight({
                    address: marketContract,
                    abi: PREDICT_MARKET_ABI,
                    functionName: 'betFromLocker',
                    args: [
                      betParams,
                      amountWei,
                      transferNonce,
                      transferDeadline,
                      transferSig,
                    ],
                    account: addr,
                  }),
                ),
              );
            }),
          );
      }),
    );
  }

  // =========================================================================
  // Per-market selectors
  // =========================================================================

  /** All cycles for a market, newest first. */
  cyclesForMarket(marketId: string): ApiCycle[] {
    return this.state().cyclesPerMarket[marketId] ?? [];
  }

  /** Whether the user has any unsettled orders on this market. */
  hasUnsettledOrders(marketId: string): boolean {
    const info = this.state().unsettledPerMarket[marketId];
    return info ? info.orderIds.length > 0 : false;
  }

  /** Whether any unsettled orders can actually be settled (their cycle is resolved). */
  hasSettleableUnsettled(marketId: string): boolean {
    return this.state().unsettledPerMarket[marketId]?.settleable ?? false;
  }

  /** Get the full unsettled info for a market (for settle dialog). */
  getUnsettledInfo(marketId: string): { orderIds: number[]; settleable: boolean; pendingCycleIds: number[] } | null {
    return this.state().unsettledPerMarket[marketId] ?? null;
  }

  /** Cycles that are NOT yet settled but have user's orders. */
  pendingCycleIds(marketId: string): number[] {
    return this.state().unsettledPerMarket[marketId]?.pendingCycleIds ?? [];
  }

  /** Whether the unsettled check has completed for this market. */
  unsettledCheckDone(marketId: string): boolean {
    return this.state().unsettledPerMarket[marketId]?.checkedAt != null;
  }

  /** Rollocker balance for a specific market's locker — spendable on a
   *  new bet right NOW. Derived at call time so a dialog opened before
   *  the locker's endTime correctly returns 0 once endTime passes,
   *  without needing a refetch (Codex 2026-05-25). */
  lockerBalanceForMarket(marketId: string): number | null {
    const wei = this.state().marketLockerBalancesWei[marketId];
    if (wei == null) return null;
    const endTime = this.state().marketLockerEndTimes[marketId] ?? 0;
    const decimals = this.state().marketLockerDecimals[marketId] ?? 18;
    const derived = deriveLockerAmounts(
      [wei, endTime, 0] as const,
      decimals,
    );
    if (derived.isExpired) return 0;
    return this.state().marketLockerBalances[marketId] ?? null;
  }

  /** True = PredictMarket.locker() is non-zero; false = no Rollocker wired;
   *  undefined = not probed yet. UI uses this to disable the Rollocker tab. */
  marketHasLocker(marketId: string): boolean | undefined {
    return this.state().marketHasLockerFlags[marketId];
  }

  /** True when the locker exists but the balance read (`allLocks`) failed.
   *  UI uses this to render "balance unavailable" instead of a stale 0. */
  marketLockerBalanceError(marketId: string): boolean {
    return this.state().marketLockerBalanceErrors[marketId] === true;
  }

  /** Exact wei balance for this market's locker, for submitting on-chain.
   *  Returns 0n once the locker is expired, even if the original read
   *  happened pre-expiry, so a dialog opened seconds before endTime
   *  cannot submit a will-revert bet after endTime crosses (Codex
   *  2026-05-25). */
  lockerBalanceWeiForMarket(marketId: string): bigint | null {
    const wei = this.state().marketLockerBalancesWei[marketId];
    if (wei == null) return null;
    const endTime = this.state().marketLockerEndTimes[marketId] ?? 0;
    if (endTime > 0 && Date.now() / 1000 > endTime) return 0n;
    return wei;
  }

  // =========================================================================
  // Per-market actions
  // =========================================================================

  /** Load cycles for a market (cached, with optional force refresh). */
  loadCycles(marketId: string, chainId: number, forceRefresh = false): void {
    if (!forceRefresh && this.isCacheValid(this.state().cyclesFetchedAt[marketId] ?? null)) return;

    this.startLoading();
    this.api
      // Same reasoning as fetchMarketsWithCycles — scheduled cycles dominate
      // the head; backend can pre-schedule 50+ future cycles so use the max.
      .getCycles({ chain_id: chainId, market_id: marketId, limit: 500 })
      .pipe(
        take(1),
        tap((cycles) => {
          // Filter out SCHEDULED cycles (cycle_id is null — not yet on-chain)
          const valid = cycles.filter((c) => c.cycle_id != null);
          const sorted = [...valid].sort((a, b) => (b.cycle_id ?? 0) - (a.cycle_id ?? 0));
          this.stopLoading();
          patchState(this.state, {
            cyclesPerMarket: { ...this.state().cyclesPerMarket, [marketId]: sorted },
            cyclesFetchedAt: { ...this.state().cyclesFetchedAt, [marketId]: Date.now() },
          });
        }),
        catchError((err: Error) => {
          console.error(`[PredictionStore] Failed to load cycles for ${marketId}:`, err);
          this.stopLoading();
          patchState(this.state, { error: err.message || 'Failed to load cycles' });
          return of(null);
        }),
      )
      .subscribe();
  }

  /**
   * Check unsettled orders for a market — reads on-chain, categorizes settleable vs pending.
   * Writes results into unsettledPerMarket state.
   */
  checkUnsettledOrders(marketId: string, predictionContract: Address, userAddr: Address): void {
    const client = this.wallet.getPublicClient();

    from(
      client.readContract({
        address: predictionContract,
        abi: PREDICT_MARKET_ABI,
        functionName: 'unsettledOrderIds',
        args: [userAddr],
      }) as Promise<bigint[]>,
    )
      .pipe(
        take(1),
        switchMap((ids) => {
          if (ids.length === 0) {
            patchState(this.state, {
              unsettledPerMarket: {
                ...this.state().unsettledPerMarket,
                [marketId]: { orderIds: [], settleable: false, pendingCycleIds: [], checkedAt: Date.now() },
              },
            });
            return of(null);
          }

          // Read each order's cycleId, then check if that cycle is settled
          return from(this.resolveUnsettledDetails(predictionContract, ids, client)).pipe(
            tap(({ orderIds, settleable, pendingCycleIds }) => {
              patchState(this.state, {
                unsettledPerMarket: {
                  ...this.state().unsettledPerMarket,
                  [marketId]: { orderIds, settleable, pendingCycleIds, checkedAt: Date.now() },
                },
              });
            }),
          );
        }),
        catchError(() => {
          // Mark check as done even on failure
          patchState(this.state, {
            unsettledPerMarket: {
              ...this.state().unsettledPerMarket,
              [marketId]: { orderIds: [], settleable: false, pendingCycleIds: [], checkedAt: Date.now() },
            },
          });
          return of(null);
        }),
      )
      .subscribe();
  }

  /** Read order details and cycle settlement status from chain.
   *  Excludes orders from active (non-settled) cycles — those are expected unsettled orders. */
  private async resolveUnsettledDetails(
    predictionContract: Address,
    ids: bigint[],
    client: ReturnType<WalletService['getPublicClient']>,
  ): Promise<{ orderIds: number[]; settleable: boolean; pendingCycleIds: number[] }> {
    // Map each order to its cycleId
    const orderCycleMap = new Map<number, number>();
    const cycleIds = new Set<number>();

    for (const oid of ids) {
      const order = (await client.readContract({
        address: predictionContract,
        abi: PREDICT_MARKET_ABI,
        functionName: 'orders',
        args: [oid],
      })) as readonly unknown[];
      const cid = Number(order[1]);
      orderCycleMap.set(Number(oid), cid);
      cycleIds.add(cid);
    }

    // Check which cycles are settled
    const settledCycleIds = new Set<number>();
    const pendingCycleIds: number[] = [];

    for (const cid of cycleIds) {
      const cycle = (await client.readContract({
        address: predictionContract,
        abi: PREDICT_MARKET_ABI,
        functionName: 'cycles',
        args: [BigInt(cid)],
      })) as readonly unknown[];
      const isSettled = cycle[5] as boolean;
      if (isSettled) {
        settledCycleIds.add(cid);
      } else {
        pendingCycleIds.push(cid);
      }
    }

    // Only include orders from settled cycles — active cycle orders are expected
    const settleableOrderIds = [...orderCycleMap.entries()]
      .filter(([, cid]) => settledCycleIds.has(cid))
      .map(([oid]) => oid);

    return {
      orderIds: settleableOrderIds,
      settleable: settleableOrderIds.length > 0,
      pendingCycleIds,
    };
  }

  /**
   * Load the user's "Rollocker Balance" for this market — the STAKING amount,
   * i.e. the wei still sitting in the locker and therefore AVAILABLE for a
   * new bet. Amounts already committed to active predictions on this market
   * are intentionally NOT included; the contract won't let the user spend
   * them again, and Wesley's 2026-04-21 feedback clarified the label
   * "Rollocker Balance" in the bet panel must read Staking, not Total.
   */
  loadMarketLockerBalance(marketId: string, predictionContract: Address, userAddr: Address): void {
    const client = this.wallet.getPublicClient();

    const ZERO_ADDR = '0x0000000000000000000000000000000000000000';

    // Two separate pipes so the probe failure (locker()) can be logged
    // independently of the balance failure (allLocks). Reviewer: the outer
    // catchError was swallowing RPC flake → flag stayed undefined forever
    // → UI kept Rollocker tab enabled on a market that couldn't honour it.
    from(
      client.readContract({
        address: predictionContract,
        abi: PREDICT_MARKET_ABI,
        functionName: 'locker',
      }) as Promise<Address>,
    )
      .pipe(
        take(1),
        tap((lockerAddr) => {
          const hasLocker = !!lockerAddr && lockerAddr.toLowerCase() !== ZERO_ADDR;
          patchState(this.state, {
            marketHasLockerFlags: {
              ...this.state().marketHasLockerFlags,
              [marketId]: hasLocker,
            },
          });
        }),
        switchMap((lockerAddr) => {
          if (!lockerAddr || lockerAddr.toLowerCase() === ZERO_ADDR) {
            return of(null);
          }
          // Read decimals alongside allLocks so we truncate display with the
          // staking token's real decimals. Hard-coding 18 worked for all live
          // lockers (CTS/DEVAI/WETH) but would miscompute 12 orders of
          // magnitude on a USDC-style 6-dec locker — same class of bug as
          // Wesley's BTC target-price overflow.
          return forkJoin({
            lockInfo: from(
              client.readContract({
                address: lockerAddr,
                abi: TOKEN_LOCKER_ABI,
                functionName: 'allLocks',
                args: [userAddr],
              }) as Promise<readonly [bigint, number, number]>,
            ),
            decimals: this.getBaseTokenInfo(predictionContract).pipe(
              take(1),
              map((info) => info?.decimals ?? 18),
              catchError(() => of(18)),
            ),
          }).pipe(
            map(({ lockInfo, decimals }) => {
              // Store the RAW values (wei + endTime + decimals). The
              // selector derives spendable at read time, so a dialog
              // opened just before endTime returns 0 once the user hits
              // submit past endTime — no refetch needed (Wesley
              // 2026-05-25 recurring report + Codex stale-state catch).
              return {
                wei: lockInfo[0],
                endTime: Number(lockInfo[1]),
                decimals,
                display: truncateWeiToCents(lockInfo[0], decimals),
              };
            }),
            catchError((err) => {
              console.error('[PredictionStore] allLocks read failed', { marketId, err });
              patchState(this.state, {
                marketLockerBalanceErrors: {
                  ...this.state().marketLockerBalanceErrors,
                  [marketId]: true,
                },
              });
              return of(null);
            }),
          );
        }),
        tap((result) => {
          if (!result) return;
          // Success path — clear any error flag from a prior failed attempt.
          const errs = this.state().marketLockerBalanceErrors;
          if (errs[marketId]) {
            patchState(this.state, {
              marketLockerBalanceErrors: { ...errs, [marketId]: false },
            });
          }
          patchState(this.state, {
            marketLockerBalances: {
              ...this.state().marketLockerBalances,
              [marketId]: result.display,
            },
            marketLockerBalancesWei: {
              ...this.state().marketLockerBalancesWei,
              [marketId]: result.wei,
            },
            marketLockerEndTimes: {
              ...this.state().marketLockerEndTimes,
              [marketId]: result.endTime,
            },
            marketLockerDecimals: {
              ...this.state().marketLockerDecimals,
              [marketId]: result.decimals,
            },
          });
        }),
        catchError((err) => {
          // locker() probe itself failed (RPC flake, ABI mismatch, etc).
          // Leave flag undefined so the UI stays in "probing" state rather
          // than locking the Rollocker tab off for a transient RPC blip.
          console.error('[PredictionStore] locker() probe failed', { marketId, err });
          return of(null);
        }),
      )
      .subscribe();
  }

  // =========================================================================
  // Clear
  // =========================================================================

  /** Reset all prediction state (e.g., on wallet disconnect). */
  clear(): void {
    patchState(this.state, INITIAL_STATE);
  }
}
